import { useState, useCallback } from 'react';
import ReactFlow, { Background, Controls } from 'reactflow';
import 'reactflow/dist/style.css';

const nodeStyle = {
  background: '#1e3a5f',
  color: '#ffffff',
  border: '1px solid #00bcd4',
  borderRadius: 8,
  padding: '8px 14px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const groupNodeStyle = {
  ...nodeStyle,
  background: '#2a2f45',
  border: '1px solid #7a8fa6',
  borderRadius: '50%',
  width: 80,
  height: 80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
};

const cloudStyle = {
  ...nodeStyle,
  background: '#1a1f2e',
  border: '1px dashed #7a8fa6',
  color: '#7a8fa6',
};

// Hover data per node
const nodeData = {
  cloud:      null, // classified — no data shown
  taclane:    { hostname: 'TACLANE-01', ip: '10.0.0.1', type: 'Encryption Device', status: 'Online', vlan: 'WAN' },
  br:         { hostname: 'RTR-BORDER-01', ip: '10.0.1.1', type: 'Border Router', status: 'Online', vlan: 'WAN/LAN', alerts: 2 },
  fw:         { hostname: 'FW-EDGE-01', ip: '10.0.2.1', type: 'Firewall', status: 'Online', vlan: 'DMZ/LAN', alerts: 3, riskScore: 82 },
  idps:       { hostname: 'IDPS-01', ip: '10.0.2.5', type: 'IDS/IPS', status: 'Online', vlan: 'Mirror', alerts: 1 },
  coreswitch: { hostname: 'SW-CORE-01', ip: '10.1.0.1', type: 'Core Switch', status: 'Online', vlan: 'All VLANs' },
  vulnscan:   { hostname: 'TENABLE-01', ip: '10.1.0.50', type: 'Vulnerability Scanner', status: 'Online', lastScan: '2 hrs ago', openVulns: 87 },
  'srv-sw':   { hostname: 'SW-SRV-01', ip: '10.1.1.1', type: 'Server Access Switch', status: 'Online', vlan: 'VLAN 10' },
  'ws-sw':    { hostname: 'SW-WS-01', ip: '10.1.2.1', type: 'WS Access Switch', status: 'Online', vlan: 'VLAN 20' },
  servers:    { hostname: 'Multiple', ip: '10.1.1.x', type: 'Servers', status: 'Online', count: 12, openVulns: 24, stigCompliance: '88%' },
  hv:         { hostname: 'HV-01', ip: '10.1.1.50', type: 'Hypervisor', status: 'Online', vms: 8, openVulns: 6, stigCompliance: '91%' },
  ws:         { hostname: 'Multiple', ip: '10.1.2.x', type: 'Workstations', count: 142, avStatus: 'Active', stigCompliance: '79%', remediationVlan: 33 },
};

const nodes = [
  // Main vertical spine (x=160)
  { id: 'cloud',      position: { x: 160, y: 0   }, data: { label: '☁️ ISSP' },                  style: cloudStyle },
  { id: 'taclane',    position: { x: 160, y: 90  }, data: { label: '🔒 Tac Lane' },               style: nodeStyle },
  { id: 'br',         position: { x: 160, y: 180 }, data: { label: '🔀 Border Router' },          style: nodeStyle },
  { id: 'fw',         position: { x: 160, y: 280 }, data: { label: '🔥 Firewall' },               style: { ...nodeStyle, border: '1px solid #f44336' } },
  { id: 'coreswitch', position: { x: 160, y: 390 }, data: { label: '🔌 Core Switch' },            style: nodeStyle },
  // Branches off core switch
  { id: 'srv-sw',     position: { x: 30,  y: 500 }, data: { label: '🔌 Server Switch' },          style: nodeStyle },
  { id: 'ws-sw',      position: { x: 290, y: 500 }, data: { label: '🔌 WS Switch' },              style: nodeStyle },
  // Leaf nodes
  { id: 'servers',    position: { x: 0,   y: 610 }, data: { label: '🖥️ Servers' },                style: nodeStyle },
  { id: 'hv',         position: { x: 160, y: 610 }, data: { label: '💾 Hypervisor' },             style: nodeStyle },
  { id: 'ws',         position: { x: 290, y: 610 }, data: { label: '💻 WS ●142' },                style: groupNodeStyle },
  // Out-of-band monitoring (right of spine)
  { id: 'idps',       position: { x: 360, y: 280 }, data: { label: '🛡️ IDPS' },                   style: nodeStyle },
  { id: 'vulnscan',   position: { x: 360, y: 390 }, data: { label: '🔍 Vuln Scanner' },           style: { ...nodeStyle, border: '1px solid #ff9800' } },
];

const edges = [
  { id: 'e1',  source: 'cloud',      target: 'taclane',    animated: true, style: { stroke: '#7a8fa6', strokeDasharray: '5,5' } },
  { id: 'e2',  source: 'taclane',    target: 'br',         animated: true, style: { stroke: '#7a8fa6', strokeDasharray: '5,5' } },
  { id: 'e3',  source: 'br',         target: 'fw',         animated: true, style: { stroke: '#00bcd4' } },
  { id: 'e4',  source: 'fw',         target: 'coreswitch', animated: true, style: { stroke: '#00bcd4' } },
  { id: 'e11', source: 'coreswitch', target: 'vulnscan',   style: { stroke: '#ff9800', strokeDasharray: '4,4' } },
  { id: 'e5',  source: 'coreswitch', target: 'srv-sw',     style: { stroke: '#4caf50' } },
  { id: 'e6',  source: 'coreswitch', target: 'ws-sw',      style: { stroke: '#4caf50' } },
  { id: 'e7',  source: 'srv-sw',     target: 'servers',    style: { stroke: '#4caf50' } },
  { id: 'e8',  source: 'srv-sw',     target: 'hv',         style: { stroke: '#4caf50' } },
  { id: 'e9',  source: 'ws-sw',      target: 'ws',         style: { stroke: '#4caf50' } },
  { id: 'e10', source: 'fw',         target: 'idps',       style: { stroke: '#a855f7' } },
];

// Hover tooltip
function Tooltip({ node, position }) {
  if (!node) return null;
  const data = nodeData[node.id];

  if (!data) return (
    <div style={{
      position: 'absolute', left: position.x + 12, top: position.y - 10, zIndex: 20,
      backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 8,
      padding: '10px 14px', pointerEvents: 'none',
    }}>
      <div style={{ color: '#7a8fa6', fontSize: 12 }}>Classified — no data displayed</div>
    </div>
  );

  return (
    <div style={{
      position: 'absolute', left: position.x + 12, top: position.y - 10, zIndex: 20,
      backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 8,
      padding: '12px 16px', pointerEvents: 'none', minWidth: 200,
    }}>
      <div style={{ color: '#00bcd4', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{data.hostname}</div>
      {[
        ['Type',        data.type],
        ['IP',          data.ip],
        ['VLAN',        data.vlan],
        ['Status',      data.status],
        ['Alerts',      data.alerts],
        ['Risk Score',  data.riskScore],
        ['Open Vulns',  data.openVulns],
        ['STIG',        data.stigCompliance],
        ['AV Status',   data.avStatus],
        ['Last Scan',   data.lastScan],
        ['VMs',         data.vms],
        ['Device Count',data.count],
        ['Remediation VLAN', data.remediationVlan ? `${data.remediationVlan} devices` : null],
      ].filter(([, v]) => v !== undefined && v !== null).map(([label, value]) => (
        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 4 }}>
          <span style={{ color: '#7a8fa6', fontSize: 11 }}>{label}</span>
          <span style={{ color: '#ffffff', fontSize: 11, fontWeight: 600 }}>{value}</span>
        </div>
      ))}
    </div>
  );
}

// Legend
function Legend() {
  const items = [
    { color: '#7a8fa6', dash: true,  label: 'Encrypted WAN' },
    { color: '#00bcd4', dash: false, label: 'Main Traffic Flow' },
    { color: '#4caf50', dash: false, label: 'Internal LAN' },
    { color: '#a855f7', dash: false, label: 'IDS/IPS Monitoring' },
    { color: '#ff9800', dash: true,  label: 'Vuln Scanner Tap' },
  ];
  return (
    <div style={{ position: 'absolute', bottom: 12, right: 12, zIndex: 10, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 8, padding: '10px 14px' }}>
      <div style={{ color: '#7a8fa6', fontSize: 11, fontWeight: 700, marginBottom: 6 }}>LEGEND</div>
      {items.map((item) => (
        <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <div style={{ width: 28, height: 2, backgroundColor: item.color, borderTop: item.dash ? `2px dashed ${item.color}` : 'none', background: item.dash ? 'none' : item.color }} />
          <span style={{ color: '#c0cfe0', fontSize: 11 }}>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

// Which topology nodes map to purpose/category groups
const NODE_GROUP_MAP = {
  ws:      { id: 'category-workstations', label: 'Workstations', type: 'category', value: 'Workstations' },
  servers: { id: 'category-servers',      label: 'Servers',      type: 'category', value: 'Servers' },
};

export default function NetworkTopology({ onSelectDevice, onSelectGroup, height = '750px' }) {
  const [tooltip, setTooltip] = useState({ node: null, position: { x: 0, y: 0 } });

  const onInit = useCallback((instance) => {
    instance.fitView({ padding: 0.15 });
    const { x, zoom } = instance.getViewport();
    instance.setViewport({ x, y: 20, zoom });
  }, []);

  const onNodeMouseEnter = useCallback((event, node) => {
    const rect = event.target.closest('.react-flow').getBoundingClientRect();
    setTooltip({
      node,
      position: { x: event.clientX - rect.left, y: event.clientY - rect.top },
    });
  }, []);

  const onNodeMouseLeave = useCallback(() => {
    setTooltip({ node: null, position: { x: 0, y: 0 } });
  }, []);

  const onNodeClick = useCallback((event, node) => {
    const data = nodeData[node.id];
    if (!data) return;

    // Group nodes → navigate to purpose/category group page
    const groupDef = NODE_GROUP_MAP[node.id];
    if (groupDef) {
      if (onSelectGroup) onSelectGroup(groupDef);
      return;
    }

    if (!onSelectDevice || !data.ip || data.hostname === 'Multiple') return;
    onSelectDevice({
      hostname: data.hostname,
      ip: data.ip,
      ips: [data.ip],
      type: data.type,
      purpose: data.type,
      status: data.status?.toLowerCase() || 'online',
    });
  }, [onSelectDevice, onSelectGroup]);

  return (
    <div style={{ width: '100%', height, position: 'relative', backgroundColor: '#111827' }}>
      <ReactFlow
        style={{ backgroundColor: '#111827' }}
        nodes={nodes}
        edges={edges}
        onInit={onInit}
        minZoom={0.3}
        maxZoom={2}
        attributionPosition="bottom-left"
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onNodeClick={onNodeClick}
      >
        <Background color="#1e3a5f" gap={20} />
        <Controls style={{ backgroundColor: '#111827', border: '1px solid #1e3a5f' }} />
      </ReactFlow>
      <Tooltip node={tooltip.node} position={tooltip.position} />
      <Legend />
    </div>
  );
}
